import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Paper, ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  PeopleAltRounded,
  CheckCircleRounded,
  EmojiEventsRounded,
  RedeemRounded,
  ArrowUpwardRounded,
  ArrowDownwardRounded,
  ArrowForwardRounded
} from '@mui/icons-material';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts';
import api from '../../../services/api';
import {
  format,
  subDays,
  subHours,
  startOfMonth,
  eachDayOfInterval,
  startOfDay,
  subMonths,
  addMonths,
  differenceInCalendarDays
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Loader from './Loader';

const METRICA_VAZIA = {
  current: 0,
  previous: null,
  delta: null,
  deltaPercent: null,
  trend: 'flat',
  hasHistory: false,
  comparisonAvailable: false
};

const buildMetricasVazias = () => ({
  clientes: { ...METRICA_VAZIA },
  checkins: { ...METRICA_VAZIA },
  brindes: { ...METRICA_VAZIA },
  brindesResgatados: { ...METRICA_VAZIA }
});

const PERIODOS_INFO = {
  hoje: {
    api: 'today',
    titulo: 'de hoje',
    comparacao: 'vs ontem'
  },
  ontem: {
    api: 'yesterday',
    titulo: 'de ontem',
    comparacao: 'vs dia anterior'
  },
  semana: {
    api: 'week',
    titulo: 'dessa semana',
    comparacao: 'vs semana passada'
  },
  mes: {
    api: 'month',
    titulo: 'desse mês',
    comparacao: 'vs mês passado'
  },
  custom: {
    api: 'custom',
    titulo: 'no período',
    comparacao: 'vs período anterior'
  }
};

const formatadorNumero = new Intl.NumberFormat('pt-BR');
const formatarNumero = (value) => formatadorNumero.format(Number.isFinite(value) ? value : 0);

const DashboardTab = () => {
  const [checkins, setCheckins] = useState([]);
  const [metricas, setMetricas] = useState(buildMetricasVazias());
  const [metricasCarregadas, setMetricasCarregadas] = useState(false);
  const [periodo, setPeriodo] = useState('hoje');
  const [periodoGrafico, setPeriodoGrafico] = useState('30d');
  const [visualizacaoDia, setVisualizacaoDia] = useState('tendencia');
  const [visualizacaoMes, setVisualizacaoMes] = useState('tendencia');
  const [loadingDados, setLoadingDados] = useState(true);
  const [loadingMetricas, setLoadingMetricas] = useState(true);
  const hojeAjustado = useMemo(() => {
    const agora = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' })
    );
    return startOfDay(agora);
  }, []);

  useEffect(() => {
    const fetchDados = async () => {
      setLoadingDados(true);
      try {
        const resCheckins = await api.get('/fidelidade/checkins');

        const checkinsData = Array.isArray(resCheckins.data) ? resCheckins.data : [];

        setCheckins(checkinsData);

        if (checkinsData.length) {
          const primeiraDataCheckin = checkinsData.reduce((maisAntiga, atual) => {
            const dataAtual = startOfDay(subHours(new Date(atual.checkin_iniciado_em), 3));
            return dataAtual < maisAntiga ? dataAtual : maisAntiga;
          }, startOfDay(subHours(new Date(checkinsData[0].checkin_iniciado_em), 3)));

          const diasDesdePrimeiroCheckin = differenceInCalendarDays(hojeAjustado, primeiraDataCheckin);
          setPeriodoGrafico(diasDesdePrimeiroCheckin < 30 ? '7d' : '30d');
        } else {
          setPeriodoGrafico('30d');
        }
      } catch (error) {
        console.error("Erro ao carregar dados do dashboard", error);
      } finally {
        setLoadingDados(false);
      }
    };

    fetchDados();
  }, [hojeAjustado]);

  useEffect(() => {
    const fetchMetricas = async () => {
      setLoadingMetricas(true);
      const periodoInfo = PERIODOS_INFO[periodo] || PERIODOS_INFO.hoje;
      try {
        const res = await api.get('/fidelidade/dashboard/metricas', {
          params: { period: periodoInfo.api }
        });
        const dados = res.data?.metrics || {};
        const montarMetrica = (chave) => ({
          ...METRICA_VAZIA,
          ...(dados?.[chave] || {})
        });

        setMetricas({
          clientes: montarMetrica('clientes'),
          checkins: montarMetrica('checkins'),
          brindes: montarMetrica('brindes'),
          brindesResgatados: montarMetrica('brindesResgatados')
        });
      } catch (error) {
        console.error("Erro ao carregar métricas do dashboard", error);
      } finally {
        setMetricasCarregadas(true);
        setLoadingMetricas(false);
      }
    };

    fetchMetricas();
  }, [periodo]);

  const periodoInfo = PERIODOS_INFO[periodo] || PERIODOS_INFO.hoje;
  const metricasAtualizando = loadingMetricas && metricasCarregadas;

  const trendStyles = useMemo(() => ({
    up: {
      color: '#16a34a',
      bg: alpha('#16a34a', 0.12),
      icon: <ArrowUpwardRounded sx={{ fontSize: 16 }} />
    },
    down: {
      color: '#dc2626',
      bg: alpha('#dc2626', 0.12),
      icon: <ArrowDownwardRounded sx={{ fontSize: 16 }} />
    },
    flat: {
      color: '#6b7280',
      bg: alpha('#6b7280', 0.12),
      icon: <ArrowForwardRounded sx={{ fontSize: 16 }} />
    }
  }), []);

  const getComparacaoInfo = (metrica) => {
    if (!metrica?.comparisonAvailable) {
      return {
        trend: 'flat',
        texto: 'Sem histórico para comparação',
        mostrarComparacao: false
      };
    }

    const atual = Number.isFinite(metrica.current) ? metrica.current : 0;
    const anterior = Number.isFinite(metrica.previous) ? metrica.previous : 0;
    const delta = Number.isFinite(metrica.delta) ? metrica.delta : atual - anterior;

    if (atual === 0 && anterior === 0) {
      return {
        trend: 'flat',
        texto: 'Sem variação',
        mostrarComparacao: true
      };
    }

    if (anterior === 0 && atual > 0) {
      const deltaLabel = `${delta > 0 ? '+' : ''}${delta}`;
      return {
        trend: 'up',
        texto: `${deltaLabel} · Novo`,
        mostrarComparacao: true
      };
    }

    if (delta === 0) {
      return {
        trend: 'flat',
        texto: 'Sem variação',
        mostrarComparacao: true
      };
    }

    const deltaLabel = `${delta > 0 ? '+' : ''}${delta}`;
    const percent = Number.isFinite(metrica.deltaPercent) ? metrica.deltaPercent : null;
    const percentLabel = percent !== null ? `${percent > 0 ? '+' : ''}${percent}%` : null;
    const texto = [deltaLabel, percentLabel].filter(Boolean).join(' · ');
    const trend = metrica.trend || (delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat');

    return {
      trend,
      texto,
      mostrarComparacao: true
    };
  };

  const cardsMetricas = useMemo(() => ([
    {
      chave: 'clientes',
      titulo: `Novos clientes ${periodoInfo.titulo}`,
      cor: '#2563eb',
      icon: <PeopleAltRounded fontSize="small" />
    },
    {
      chave: 'checkins',
      titulo: `Check-ins ${periodoInfo.titulo}`,
      cor: '#ff2d54',
      icon: <CheckCircleRounded fontSize="small" />
    },
    {
      chave: 'brindes',
      titulo: `Brindes gerados ${periodoInfo.titulo}`,
      cor: '#f59e0b',
      icon: <EmojiEventsRounded fontSize="small" />
    },
    {
      chave: 'brindesResgatados',
      titulo: `Brindes resgatados ${periodoInfo.titulo}`,
      cor: '#10b981',
      icon: <RedeemRounded fontSize="small" />
    }
  ]), [periodoInfo.titulo]);

  if (loadingDados || (loadingMetricas && !metricasCarregadas)) return <Loader />;

  const periodosGrafico = {
    '7d': { label: '7 dias', inicio: subDays(hojeAjustado, 6) },
    '30d': { label: '30 dias', inicio: subDays(hojeAjustado, 29) },
    '3m': { label: '3 meses', inicio: startOfDay(subMonths(hojeAjustado, 3)) },
    '6m': { label: '6 meses', inicio: startOfDay(subMonths(hojeAjustado, 6)) }
  };

  const ordemPeriodosGrafico = ['7d', '30d', '3m', '6m'];

  const limiteGrafico = periodosGrafico[periodoGrafico]?.inicio || subDays(hojeAjustado, 6);

  const handlePeriodoGraficoChange = (_, novoPeriodo) => {
    if (novoPeriodo) setPeriodoGrafico(novoPeriodo);
  };

  const handleVisualizacaoDiaChange = (_, novaVisualizacao) => {
    if (novaVisualizacao) setVisualizacaoDia(novaVisualizacao);
  };

  const handleVisualizacaoMesChange = (_, novaVisualizacao) => {
    if (novaVisualizacao) setVisualizacaoMes(novaVisualizacao);
  };

  const checkinsPorDia = checkins.reduce((acc, c) => {
    const dia = format(subHours(new Date(c.checkin_iniciado_em), 3), 'dd/MM/yyyy');
    acc[dia] = (acc[dia] || 0) + 1;
    return acc;
  }, {});

  const intervaloDias = eachDayOfInterval({ start: limiteGrafico, end: hojeAjustado });
  const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
  const isSeteDias = periodoGrafico === '7d';

  const dataDia = intervaloDias.map(data => {
    const diaFormatado = format(data, 'dd/MM/yyyy');
    return {
      dia: diaFormatado,
      diaCurto: format(data, 'dd/MM'),
      diaSemana: diasSemana[data.getDay()],
      quantidade: checkinsPorDia[diaFormatado] || 0
    };
  });

  const checkinsPorMes = checkins.reduce((acc, c) => {
    const mes = format(subHours(new Date(c.checkin_iniciado_em), 3), 'MMMM yyyy', { locale: ptBR });
    acc[mes] = (acc[mes] || 0) + 1;
    return acc;
  }, {});
  const mesAtualLabel = format(hojeAjustado, 'MMMM yyyy', { locale: ptBR });

  const primeiraDataMes = checkins.length > 0
    ? startOfMonth(
        checkins.reduce((maisAntiga, atual) => {
          const dataAtual = subHours(new Date(atual.checkin_iniciado_em), 3);
          return dataAtual < maisAntiga ? dataAtual : maisAntiga;
        }, subHours(new Date(checkins[0].checkin_iniciado_em), 3))
      )
    : startOfMonth(hojeAjustado);

  const ultimaDataMes = checkins.length > 0
    ? startOfMonth(
        checkins.reduce((maisRecente, atual) => {
          const dataAtual = subHours(new Date(atual.checkin_iniciado_em), 3);
          return dataAtual > maisRecente ? dataAtual : maisRecente;
        }, subHours(new Date(checkins[0].checkin_iniciado_em), 3))
      )
    : startOfMonth(hojeAjustado);

  const mesesTimeline = [];
  let cursorMes = primeiraDataMes;

  while (mesesTimeline.length < 12 && cursorMes <= ultimaDataMes) {
    mesesTimeline.push(cursorMes);
    cursorMes = addMonths(cursorMes, 1);
  }

  if (mesesTimeline.length === 0) {
    mesesTimeline.push(primeiraDataMes);
  }

  const dataMes = mesesTimeline.map(data => {
    const mesFormatado = format(data, 'MMMM yyyy', { locale: ptBR });
    return {
      mes: mesFormatado,
      quantidade: checkinsPorMes[mesFormatado] || 0,
      isMesAtual: mesFormatado === mesAtualLabel
    };
  });

  const renderMesAtualDot = ({ cx, cy, payload, r, stroke, strokeWidth, fill }) => {
    if (cx == null || cy == null || !payload) return null;

    const raio = Number.isFinite(r) ? r : 3;
    const corStroke = stroke || '#FF2D54';
    const larguraStroke = Number.isFinite(strokeWidth) ? strokeWidth : 2;
    const baseDot = (
      <circle
        cx={cx}
        cy={cy}
        r={raio}
        stroke={corStroke}
        strokeWidth={larguraStroke}
        fill={fill || '#ffffff'}
      />
    );

    if (!payload.isMesAtual) {
      return baseDot;
    }

    return (
      <g>
        {baseDot}
        <circle
          cx={cx}
          cy={cy}
          r={raio + 5}
          fill="none"
          stroke={alpha(corStroke, 0.25)}
          strokeWidth={2}
        />
      </g>
    );
  };

  const renderDiaSemanaTick = ({ x, y, payload }) => {
    const item = dataDia[payload.index];
    if (!item) return null;
    return (
      <g transform={`translate(${x},${y})`}>
        <text x={0} y={0} dy={8} textAnchor="middle" fill="#6b7280" fontSize={12}>
          <tspan x="0" dy="10">{item.diaSemana}</tspan>
          <tspan x="0" dy="14">{item.diaCurto}</tspan>
        </text>
      </g>
    );
  };

  const eixoXDiaProps = {
    dataKey: 'dia',
    interval: isSeteDias ? 0 : undefined,
    height: isSeteDias ? 50 : 30,
    tick: isSeteDias ? renderDiaSemanaTick : undefined
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Visão Geral
      </Typography>

      <Paper
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: 3,
          border: '1px solid #e5e7eb',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8f5f0 100%)',
          boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)'
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap={{ xs: 'wrap', md: 'nowrap' }}
          gap={2}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold">Período de análise</Typography>
            <Typography variant="caption" color="text.secondary">
              Escolha o intervalo para atualizar os cards.
            </Typography>
            <Box
              sx={{
                px: 1.5,
                py: 0.6,
                borderRadius: 999,
                bgcolor: alpha('#ff2d54', 0.12),
                color: '#be123c',
                fontWeight: 700,
                fontSize: 12
              }}
            >
              {periodoInfo.titulo.charAt(0).toUpperCase() + periodoInfo.titulo.slice(1)}
            </Box>
          </Box>
          <ToggleButtonGroup
            value={periodo}
            exclusive
            onChange={(e, v) => v && setPeriodo(v)}
            aria-label="período"
            sx={{
              flexWrap: 'wrap',
              gap: 1,
              width: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                border: '1px solid #e5e7eb',
                color: '#334155'
              },
              '& .Mui-selected': {
                bgcolor: '#111827',
                color: '#ffffff',
                borderColor: '#111827'
              }
            }}
          >
            <ToggleButton value="hoje">Hoje</ToggleButton>
            <ToggleButton value="ontem">Ontem</ToggleButton>
            <ToggleButton value="semana">Essa semana</ToggleButton>
            <ToggleButton value="mes">Esse mês</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      <Grid container spacing={3} mb={4}>
        {cardsMetricas.map((card) => {
          const metrica = metricas[card.chave] || METRICA_VAZIA;
          const comparacao = getComparacaoInfo(metrica);
          const trend = trendStyles[comparacao.trend] || trendStyles.flat;
          const semDados = !Number.isFinite(metrica.current) || metrica.current === 0;

          return (
            <Grid item xs={12} sm={6} md={3} key={card.chave}>
              <Card
                sx={{
                  borderRadius: 3,
                  border: '1px solid #edf0f4',
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 8px 20px rgba(15, 23, 42, 0.06)',
                  opacity: metricasAtualizando ? 0.7 : 1,
                  transition: 'opacity 0.2s ease'
                }}
              >
                <Box sx={{ position: 'absolute', top: 0, left: 0, height: 4, width: '100%', bgcolor: card.cor }} />
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase' }}
                    >
                      {card.titulo}
                    </Typography>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: alpha(card.cor, 0.12),
                        color: card.cor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Box>
                  {semDados ? (
                    <Typography variant="subtitle2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                      Nenhuma atividade registrada neste período
                    </Typography>
                  ) : (
                    <Typography variant="h4" fontWeight="bold">
                      {formatarNumero(metrica.current)}
                    </Typography>
                  )}
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.35,
                        borderRadius: 999,
                        bgcolor: trend.bg,
                        color: trend.color
                      }}
                    >
                      {trend.icon}
                      <Typography variant="caption" fontWeight={600} sx={{ color: trend.color }}>
                        {comparacao.texto}
                      </Typography>
                    </Box>
                    {comparacao.mostrarComparacao && (
                      <Typography variant="caption" color="text.secondary">
                        {periodoInfo.comparacao}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            📅 Check-ins por Dia
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <ToggleButtonGroup
              value={visualizacaoDia}
              exclusive
              onChange={handleVisualizacaoDiaChange}
              size="small"
            >
              <ToggleButton value="tendencia">Tendência</ToggleButton>
              <ToggleButton value="barra">Barras</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              value={periodoGrafico}
              exclusive
              onChange={handlePeriodoGraficoChange}
              size="small"
            >
              {ordemPeriodosGrafico.map(chave => (
                <ToggleButton key={chave} value={chave}>
                  {periodosGrafico[chave].label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
        {visualizacaoDia === 'barra' ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataDia}>
              <XAxis {...eixoXDiaProps} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="quantidade" fill="#FF2D54" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dataDia}>
              <XAxis {...eixoXDiaProps} />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="quantidade"
                stroke="#FF2D54"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight="bold" mb={2}>
          📈 Check-ins por Mês
        </Typography>
        <ToggleButtonGroup
          value={visualizacaoMes}
          exclusive
          onChange={handleVisualizacaoMesChange}
          size="small"
          sx={{ mb: 2 }}
        >
          <ToggleButton value="tendencia">Tendência</ToggleButton>
          <ToggleButton value="barra">Barras</ToggleButton>
        </ToggleButtonGroup>
        {visualizacaoMes === 'barra' && (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dataMes}>
              <XAxis dataKey="mes" />
              <YAxis />
              <Tooltip
                labelFormatter={(label) => (
                  label === mesAtualLabel ? `${label} - Mes atual` : label
                )}
              />
              <Bar dataKey="quantidade" fill="#FF2D54" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
        {visualizacaoMes !== 'barra' && (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dataMes}>
            <XAxis dataKey="mes" />
            <YAxis />
            <Tooltip
              labelFormatter={(label) => (
                label === mesAtualLabel ? `${label} - Mês atual` : label
              )}
            />
            <Line
              type="monotone"
              dataKey="quantidade"
              stroke="#FF2D54"
              strokeWidth={2}
              dot={renderMesAtualDot}
            />
          </LineChart>
        </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardTab;
